import React, { useRef } from 'react';
import { StepProps } from './types';

const Step5SEO: React.FC<StepProps> = ({ form, handleChange, errors, seoImageFile, setSeoImageFile, seoImagePreview, setSeoImagePreview, QuickUpdateButton }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (setSeoImageFile) setSeoImageFile(file);
            if (setSeoImagePreview) setSeoImagePreview(URL.createObjectURL(file));
        }
    };

    const removeImage = () => {
        if (setSeoImageFile) setSeoImageFile(null);
        if (setSeoImagePreview) setSeoImagePreview('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4 px-1">
                <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg shadow-md">
                    5
                </div>
                <div className="flex-1 flex justify-between items-center">
                    <div>
                        <h5 className="text-xl font-bold text-gray-800">SEO Details</h5>
                        <p className="text-xs text-gray-500">Optimize your project for better visibility in search engines</p>
                    </div>
                    {QuickUpdateButton && <QuickUpdateButton />}
                    <button type="button" className="text-sm font-bold text-blue-600 bg-blue-50 px-4 py-2 rounded-lg border border-blue-100 hover:bg-blue-100 transition-colors flex items-center gap-2">
                        <i className="fa-solid fa-eye"></i> Preview SEO
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Left Column */}
                <div className="space-y-4">
                    {/* Meta Title */}
                    <div className="space-y-1.5">
                        <div className="flex justify-between items-end">
                            <label className="text-xs font-bold text-gray-600 block">Meta Title <span className="text-rose-500">*</span></label>
                            <span className="text-[10px] font-semibold text-gray-400">{form.metaTitle?.length || 0}/60 characters</span>
                        </div>
                        <input
                            type="text"
                            maxLength={60}
                            className={`w-full px-3 py-2 rounded-xl bg-gray-50 border ${errors?.metaTitle ? 'border-rose-500 bg-rose-50' : 'border-gray-200'} text-gray-800 focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm`}
                            name="metaTitle"
                            value={form.metaTitle || ''}
                            onChange={handleChange}
                            placeholder="e.g. Premium Residential Apartments in Pune | 1, 2, 3 & 4 BHK Homes"
                        />
                    </div>

                    {/* Meta Description */}
                    <div className="space-y-1.5">
                        <div className="flex justify-between items-end">
                            <label className="text-xs font-bold text-gray-600 block">Meta Description <span className="text-rose-500">*</span></label>
                            <span className="text-[10px] font-semibold text-gray-400">{form.metaDescription?.length || 0}/160 characters</span>
                        </div>
                        <textarea
                            maxLength={160}
                            className={`w-full px-3 py-2 rounded-xl bg-gray-50 border ${errors?.metaDescription ? 'border-rose-500 bg-rose-50' : 'border-gray-200'} text-gray-800 focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm min-h-[90px] resize-none`}
                            name="metaDescription"
                            value={form.metaDescription || ''}
                            onChange={handleChange}
                            placeholder="e.g. Explore premium 1, 2, 3 & 4 BHK apartments in Pune. Modern amenities, prime location..."
                        />
                    </div>

                    {/* Meta Keywords */}
                    <div className="space-y-1.5">
                        <div className="flex justify-between items-end">
                            <label className="text-xs font-bold text-gray-600 block">Meta Keywords (Optional)</label>
                            <span className="text-[10px] font-semibold text-gray-400">{form.metaKeywords?.length || 0}/200 characters</span>
                        </div>
                        <textarea
                            maxLength={200}
                            className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 text-gray-800 focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm min-h-[70px] resize-none"
                            name="metaKeywords"
                            value={form.metaKeywords || ''}
                            onChange={handleChange}
                            placeholder="flats in pune, 2 bhk in pune, residential apartments, separate keywords with commas"
                        />
                        <p className="text-[10px] text-gray-400 font-semibold mt-1">Separate keywords with commas</p>
                    </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                    {/* URL Slug */}
                    <div className="space-y-1.5">
                        <div className="flex justify-between items-end">
                            <label className="text-xs font-bold text-gray-600 block">URL Slug <span className="text-rose-500">*</span></label>
                            <span className="text-[10px] font-semibold text-gray-400">{form.slug?.length || 0}/75 characters</span>
                        </div>
                        <input
                            type="text"
                            maxLength={75}
                            className={`w-full px-3 py-2 rounded-xl bg-gray-50 border ${errors?.slug ? 'border-rose-500 bg-rose-50' : 'border-gray-200'} text-gray-800 focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm`}
                            name="slug"
                            value={form.slug || ''}
                            onChange={handleChange}
                            placeholder="e.g. premium-residential-apartments-pune"
                        />
                        <div className="mt-1 flex items-center gap-1.5 overflow-hidden">
                            <span className="text-xs text-emerald-600 font-semibold truncate">https://yourwebsite.com/projects/{form.slug || 'premium-residential-apartments-pune'}</span>
                            <i className="fa-regular fa-copy text-gray-400 cursor-pointer hover:text-gray-600 text-[10px]"></i>
                        </div>
                    </div>


                    {/* Focus Keyword */}
                    <div className="space-y-1.5">
                        <div className="flex justify-between items-end">
                            <label className="text-xs font-bold text-gray-600 block">Focus Keyword <span className="text-rose-500">*</span></label>
                            <span className="text-[10px] font-semibold text-gray-400">{form.focusKeyword?.length || 0}/100 characters</span>
                        </div>
                        <input
                            type="text"
                            maxLength={100}
                            className={`w-full px-3 py-2 rounded-xl bg-gray-50 border ${errors?.focusKeyword ? 'border-rose-500 bg-rose-50' : 'border-gray-200'} text-gray-800 focus:border-blue-500 focus:bg-white transition-all font-semibold outline-none text-sm`}
                            name="focusKeyword"
                            value={form.focusKeyword || ''}
                            onChange={handleChange}
                            placeholder="e.g. premium apartments in pune"
                        />
                        <p className="text-[10px] text-gray-400 font-semibold mt-1">Main keyword you want to rank for</p>
                    </div>
                </div>
            </div>

            {/* SEO Image */}
            <div className="mt-4 space-y-2">
                <label className="text-xs font-bold text-gray-600 block">SEO Image</label>
                <div className="flex flex-wrap md:flex-nowrap gap-3">
                    {/* Upload Box */}
                    <div 
                        className={`border-2 border-dashed rounded-xl flex flex-col items-center justify-center p-6 text-center cursor-pointer transition-all w-full md:w-64 h-32 ${seoImagePreview ? 'border-gray-200 bg-gray-50 hidden' : 'border-blue-300 bg-blue-50 hover:bg-blue-100 hover:border-blue-400'}`}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <i className="fa-regular fa-image text-2xl text-blue-500 mb-2"></i>
                        <span className="text-xs font-bold text-gray-700">Upload Image</span>
                        <span className="text-[9px] text-gray-500 font-semibold mt-1">Recommended size: 1200x630px</span>
                        <span className="text-[9px] text-gray-400 font-medium">Max size: 2MB (JPG, PNG)</span>
                        <input 
                            type="file" 
                            accept="image/*" 
                            className="hidden" 
                            ref={fileInputRef} 
                            onChange={handleImageChange} 
                        />
                    </div>

                    {/* Preview Box */}
                    {(seoImagePreview || form.seoImageUrl) && (
                        <div className="flex items-start gap-4 p-3 border border-gray-200 rounded-xl bg-white w-full max-w-md">
                            <div className="w-32 h-20 bg-gray-100 rounded-lg overflow-hidden shrink-0 border border-gray-200">
                                <img src={seoImagePreview || form.seoImageUrl} alt="SEO Preview" className="w-full h-full object-cover" />
                            </div>
                            <div className="flex flex-col flex-1 h-full py-1">
                                <span className="text-xs font-bold text-gray-800 line-clamp-1">{seoImageFile?.name || 'seo-image.jpg'}</span>
                                {seoImageFile && <span className="text-[10px] font-semibold text-gray-500 mt-0.5">Size: {(seoImageFile.size / (1024 * 1024)).toFixed(2)} MB</span>}
                                <div className="mt-auto pt-2">
                                    <button 
                                        type="button" 
                                        onClick={removeImage}
                                        className="text-[11px] font-bold text-rose-600 bg-rose-50 px-3 py-1 rounded-md border border-rose-100 hover:bg-rose-100 transition-colors"
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Step5SEO;
